// マップキャンバスのパン/ズーム操作と座標変換ユーティリティ。
// fork_webapp_react の useMapCanvas から、シナリオ編集プレビューに必要な範囲を移植。
// 背景画像は MQTT から直接購読せず、呼び出し側 (useScenarios) がデコードして
// props で渡す方針のため、本フックはパン/ズームの状態管理のみを担う。

import { useState, useRef, useEffect, type RefObject } from 'react'
import type { MapCanvasParams } from '@/types'

export function useMapCanvas(
  canvasParams: MapCanvasParams,
  effectiveCanvasWidthMeter?: number,
  // true の間はドラッグによるパンを無効化する（矩形選択モード用）
  panDisabledRef?: RefObject<boolean>
) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [scale, setScale] = useState(1.0)
  const [offset, setOffset] = useState({ x: 0, y: 0 })

  const isDraggingRef = useRef(false)
  const dragStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 })
  const touchStartRef = useRef<{ distance: number; scale: number } | null>(null)
  const offsetRef = useRef(offset)
  const scaleRef = useRef(scale)
  const rafRef = useRef<number | null>(null)
  const effectiveCanvasWidthMeterRef = useRef(effectiveCanvasWidthMeter)

  useEffect(() => {
    offsetRef.current = offset
  }, [offset])

  useEffect(() => {
    scaleRef.current = scale
  }, [scale])

  useEffect(() => {
    effectiveCanvasWidthMeterRef.current = effectiveCanvasWidthMeter
  }, [effectiveCanvasWidthMeter])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const getTouchDistance = (touches: TouchList) => {
      if (touches.length < 2) return 0
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.sqrt(dx * dx + dy * dy)
    }

    const handleDrag = (clientX: number, clientY: number) => {
      if (!isDraggingRef.current || !canvasRef.current) return

      const canvas = canvasRef.current
      const rect = canvas.getBoundingClientRect()
      const baseCanvasWidthMeter =
        effectiveCanvasWidthMeterRef.current ?? canvasParams.canvasWidthMeter
      const effectiveWidthMeter = baseCanvasWidthMeter / scaleRef.current
      const canvasHeightMeter =
        (canvas.height / canvas.width) * effectiveWidthMeter

      const dx = clientX - dragStartRef.current.x
      const dy = clientY - dragStartRef.current.y

      const deltaX = -(dx / rect.width) * effectiveWidthMeter
      const deltaY = -(dy / rect.height) * canvasHeightMeter

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
      }

      rafRef.current = requestAnimationFrame(() => {
        setOffset({
          x: dragStartRef.current.offsetX + deltaX,
          y: dragStartRef.current.offsetY + deltaY,
        })
        rafRef.current = null
      })
    }

    const handleMouseDown = (e: MouseEvent) => {
      if (panDisabledRef?.current) return
      isDraggingRef.current = true
      dragStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        offsetX: offsetRef.current.x,
        offsetY: offsetRef.current.y,
      }
    }

    const handleMouseMove = (e: MouseEvent) => {
      handleDrag(e.clientX, e.clientY)
    }

    const handleMouseUp = () => {
      isDraggingRef.current = false
    }

    const handleTouchStart = (e: TouchEvent) => {
      if (panDisabledRef?.current) return
      if (e.touches.length === 2) {
        const distance = getTouchDistance(e.touches)
        touchStartRef.current = { distance, scale: scaleRef.current }
        e.preventDefault()
      } else if (e.touches.length === 1) {
        isDraggingRef.current = true
        dragStartRef.current = {
          x: e.touches[0].clientX,
          y: e.touches[0].clientY,
          offsetX: offsetRef.current.x,
          offsetY: offsetRef.current.y,
        }
      }
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && touchStartRef.current) {
        const distance = getTouchDistance(e.touches)
        const scaleChange = distance / touchStartRef.current.distance
        setScale(touchStartRef.current.scale * scaleChange)
        e.preventDefault()
      } else if (e.touches.length === 1 && isDraggingRef.current) {
        handleDrag(e.touches[0].clientX, e.touches[0].clientY)
      }
    }

    const handleTouchEnd = () => {
      isDraggingRef.current = false
      touchStartRef.current = null
    }

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()
      const delta = e.deltaY > 0 ? 0.9 : 1.1
      setScale((prev) => prev * delta)
    }

    container.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)
    container.addEventListener('touchstart', handleTouchStart, {
      passive: false,
    })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd)
    container.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      container.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
      container.removeEventListener('wheel', handleWheel)

      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [canvasParams.canvasWidthMeter, effectiveCanvasWidthMeter, panDisabledRef])

  const handleZoomIn = () => setScale((prev) => prev * 1.2)
  const handleZoomOut = () => setScale((prev) => prev / 1.2)
  const handleResetView = () => {
    setScale(1.0)
    setOffset({ x: 0, y: 0 })
  }

  return {
    canvasRef,
    containerRef,
    scale,
    offset,
    handleZoomIn,
    handleZoomOut,
    handleResetView,
  }
}

// ワールド座標 → キャンバス座標 変換関数を生成
export function createWorldToCanvas(
  canvas: HTMLCanvasElement,
  center: { x: number; y: number },
  offset: { x: number; y: number },
  scale: number,
  canvasWidthMeter: number
) {
  const effectiveWidthMeter = canvasWidthMeter / scale
  const canvasHeightMeter = (canvas.height / canvas.width) * effectiveWidthMeter

  return (x: number, y: number) => ({
    x:
      ((x - center.x - offset.x) * canvas.width) / effectiveWidthMeter +
      canvas.width / 2,
    y:
      ((-y + center.y - offset.y) * canvas.height) / canvasHeightMeter +
      canvas.height / 2,
  })
}

// 背景画像を描画
export function drawBackgroundImage(
  ctx: CanvasRenderingContext2D,
  bgImage: HTMLImageElement,
  center: { x: number; y: number },
  offset: { x: number; y: number },
  scale: number,
  canvasParams: MapCanvasParams,
  effectiveCanvasWidthMeter?: number
) {
  const canvas = ctx.canvas
  const baseWidthMeter =
    effectiveCanvasWidthMeter ?? canvasParams.canvasWidthMeter
  const effectiveWidthMeter = baseWidthMeter / scale
  const canvasHeightMeter = (canvas.height / canvas.width) * effectiveWidthMeter

  const xPix = (center.x + offset.x) * canvasParams.imagePixelPerMeter
  const yPix = -(center.y - offset.y) * canvasParams.imagePixelPerMeter

  ctx.drawImage(
    bgImage,
    xPix +
      canvasParams.imageOriginPixX -
      (effectiveWidthMeter / 2) * canvasParams.imagePixelPerMeter,
    yPix +
      canvasParams.imageOriginPixY -
      (canvasHeightMeter / 2) * canvasParams.imagePixelPerMeter,
    effectiveWidthMeter * canvasParams.imagePixelPerMeter,
    canvasHeightMeter * canvasParams.imagePixelPerMeter,
    0,
    0,
    canvas.width,
    canvas.height
  )
}

// スロット/ステーションをフォークリフト型で描画
export function drawForklift(
  ctx: CanvasRenderingContext2D,
  id: string,
  x: number,
  y: number,
  theta: number,
  color: string,
  size = 50,
  bodyColor = '#4b5563',
  centerLabel?: string,
  strokeColor?: string,
  loaded = false
) {
  const bodyW = size * 1.2
  const bodyH = size * 1.1
  const forkLen = size * 1.1
  const forkW = size * 0.25
  const forkGap = size * 0.55 // フォーク爪の中心間距離
  const cwW = size * 1.2 // 本体（青）幅
  const cwH = size * 0.4 // 本体（青）高さ
  const r = size * 0.1 // 角丸半径
  const strokeWidth = Math.max(2, size * 0.06)

  ctx.save()
  ctx.translate(x, y)
  // Canvas 座標系は Y 軸下向き。theta=0 で右向き（ROS 座標系）のため、
  // -theta で ROS→Canvas に変換し、-PI/2 で「上向き=前方」に補正する。
  ctx.rotate(-theta - Math.PI / 2)
  ctx.translate(0, size * 0.5)

  const leftFork = () =>
    ctx.roundRect(
      -forkGap / 2 - forkW / 2,
      -bodyH / 2 - forkLen + 15,
      forkW,
      forkLen,
      r
    )
  const rightFork = () =>
    ctx.roundRect(
      forkGap / 2 - forkW / 2,
      -bodyH / 2 - forkLen + 15,
      forkW,
      forkLen,
      r
    )
  const bodyPath = () => ctx.roundRect(-bodyW / 2, -bodyH / 2, bodyW, bodyH, r)
  const stripPath = () =>
    ctx.roundRect(-cwW / 2, bodyH / 2 - cwH * 0.8, cwW, cwH, r)

  // 縁取り（外周シルエットのみ）。先に太めに stroke し、後から塗りで
  // 内側の継ぎ目を隠すことで外周だけが縁取りとして残る。
  if (strokeColor) {
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = strokeWidth * 2
    ctx.lineJoin = 'round'
    for (const drawPath of [leftFork, rightFork, bodyPath, stripPath]) {
      ctx.beginPath()
      drawPath()
      ctx.stroke()
    }
  }

  // フォーク爪（前方・2本）
  ctx.fillStyle = color
  ctx.beginPath()
  leftFork()
  ctx.fill()
  ctx.beginPath()
  rightFork()
  ctx.fill()

  // 本体（グレー）
  ctx.fillStyle = bodyColor
  ctx.beginPath()
  bodyPath()
  ctx.fill()

  // 本体（青ストリップ）
  ctx.fillStyle = color
  ctx.beginPath()
  stripPath()
  ctx.fill()

  // 荷物（パレット）: 爪の上に茶色の四角を重ねて描き、荷を運んでいる状態を表す
  if (loaded) {
    const cargoW = size * 0.95
    const cargoH = forkLen * 0.8
    const cargoY = -bodyH / 2 - forkLen + 15 + (forkLen - cargoH) / 2
    ctx.beginPath()
    ctx.roundRect(-cargoW / 2, cargoY, cargoW, cargoH, r)
    ctx.fillStyle = '#b45309'
    ctx.fill()
    ctx.strokeStyle = '#92400e'
    ctx.lineWidth = strokeWidth
    ctx.stroke()
  }

  // centerLabel（「高」など。ID ラベルの背後に薄く表示）
  if (centerLabel) {
    const centerY = cwH * 0.3
    ctx.font = `bold ${size * 0.7}px Arial`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillStyle = 'rgba(255, 255, 255, 0.6)'
    ctx.fillText(centerLabel, 0, centerY)
  }

  // ID ラベル
  if (id !== '' && id.length < 5) {
    ctx.font = `bold ${size / 1.5}px Arial`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'

    ctx.strokeStyle = 'white'
    ctx.lineWidth = 3
    ctx.strokeText(id, 0, 0)

    ctx.fillStyle = 'black'
    ctx.fillText(id, 0, 0)
  }

  ctx.restore()
}
